// Contexto que /api/sec/login recibe junto a email/password: dispositivo,
// ipAddress, latitude, longitude y userAgent. Se arma en un solo lugar para que
// cualquiera que llame a loginService mande exactamente lo mismo; si el backend
// corrige un nombre de campo, se toca esta funcion y nada mas.
import { getStoredGeolocation } from "../geolocation/geoStore";

const DESCONOCIDO = "Desconocido";

// Recolectar candidatos ICE locales es cuestion de milisegundos; el limite solo
// existe para que el login no se quede esperando si el navegador no responde.
const TIMEOUT_ICE = 500;

// El orden importa: Edge, Opera y Samsung Internet tambien dicen "Chrome" en su
// user agent, y Chrome dice "Safari". El primero que casa gana.
const NAVEGADORES = [
  { nombre: "Edge", patron: /\bedg(?:e|a|ios)?\// },
  { nombre: "Opera", patron: /\b(?:opr|opios)\/|\bopera\b/ },
  { nombre: "Samsung Internet", patron: /samsungbrowser/ },
  { nombre: "Firefox", patron: /\b(?:firefox|fxios)\// },
  { nombre: "Chrome", patron: /\b(?:chrome|crios|chromium)\// },
  { nombre: "Safari", patron: /safari/ },
];

const PLATAFORMAS = [
  { nombre: "iPhone", patron: /iphone/ },
  { nombre: "iPad", patron: /ipad/ },
  { nombre: "iPod", patron: /ipod/ },
  { nombre: "Android", patron: /android/ },
  { nombre: "Windows", patron: /windows/ },
  { nombre: "Mac", patron: /macintosh|mac os x/ },
  { nombre: "Linux", patron: /linux|x11/ },
];

const buscar = (catalogo, ua) =>
  catalogo.find(({ patron }) => patron.test(ua))?.nombre || null;

const detectarPlataforma = (ua) => {
  const plataforma = buscar(PLATAFORMAS, ua);
  // Desde iPadOS 13 el iPad se anuncia como Macintosh; lo unico que lo delata
  // es que responde a tactil.
  if (plataforma === "Mac" && (navigator?.maxTouchPoints || 0) > 1) return "iPad";
  return plataforma;
};

export const getUserAgent = () =>
  (typeof navigator !== "undefined" && navigator.userAgent) || "";

// Etiqueta legible del equipo, no el user agent crudo: "iPhone - Safari".
// El modelo exacto ("iPhone 15") ya no lo publica ningun navegador moderno, asi
// que lo mas fino que se puede afirmar es la familia de dispositivo.
export const getDeviceLabel = () => {
  const ua = getUserAgent().toLowerCase();
  if (!ua) return DESCONOCIDO;

  const partes = [detectarPlataforma(ua), buscar(NAVEGADORES, ua)].filter(Boolean);
  return partes.length ? partes.join(" - ") : DESCONOCIDO;
};

// Un candidato ICE se ve asi, y la direccion es el quinto campo:
// "candidate:842163049 1 udp 1677729535 192.168.1.10 54321 typ host generation 0"
const direccionDeCandidato = (candidato) => String(candidato || "").split(" ")[4] || null;

const esDireccionUtil = (direccion) => {
  if (!direccion) return false;
  // Desde 2019 los navegadores ofuscan la IP local detras de un hostname mDNS
  // (`a1b2c3d4-....local`) que ademas rota: no identifica al equipo.
  if (direccion.endsWith(".local")) return false;
  if (direccion === "0.0.0.0" || direccion === "::" || direccion === "::1") return false;
  if (direccion.startsWith("127.")) return false;
  // Link-local (APIPA / fe80::): es lo que queda cuando no hubo DHCP.
  if (/^(169\.254\.|fe80:)/i.test(direccion)) return false;
  return true;
};

const esIPv4 = (direccion) => /^\d{1,3}(\.\d{1,3}){3}$/.test(direccion);

// IP privada del equipo (192.168.x.x, 10.x.x.x...), la que aparece en el ejemplo
// del contrato. Se saca de los candidatos ICE locales de una RTCPeerConnection
// SIN servidores: no sale una sola peticion de red ni participa ningun tercero.
//
// El navegador manda: si ofusca la direccion con mDNS o tiene WebRTC apagado, el
// resultado es null y el login sale igual. La IP publica sigue siendo cosa del
// backend, que la ve en el request y no se le puede mentir.
export const getLocalIpAddress = (timeoutMs = TIMEOUT_ICE) =>
  new Promise((resolve) => {
    if (typeof RTCPeerConnection === "undefined") {
      resolve(null);
      return;
    }

    const encontradas = [];
    let conexion = null;
    let listo = false;

    const terminar = () => {
      if (listo) return;
      listo = true;
      clearTimeout(temporizador);
      try {
        conexion?.close();
      } catch {
        // El navegador ya la cerro; no hay nada que rescatar aqui.
      }
      // IPv4 primero: es el formato que espera el ejemplo del contrato.
      resolve(encontradas.find(esIPv4) || encontradas[0] || null);
    };

    const temporizador = setTimeout(terminar, timeoutMs);

    try {
      conexion = new RTCPeerConnection({ iceServers: [] });
      conexion.onicecandidate = (evento) => {
        // Un candidato nulo significa que ya no vienen mas.
        if (!evento?.candidate) {
          terminar();
          return;
        }
        const direccion = direccionDeCandidato(evento.candidate.candidate);
        if (esDireccionUtil(direccion) && !encontradas.includes(direccion)) {
          encontradas.push(direccion);
        }
      };
      // Sin un canal no hay nada que negociar y no se recolecta ningun candidato.
      conexion.createDataChannel("");
      conexion
        .createOffer()
        .then((oferta) => conexion.setLocalDescription(oferta))
        .catch(terminar);
    } catch {
      // Firefox con WebRTC deshabilitado revienta en el constructor.
      terminar();
    }
  });

const coordenadasDeSesion = () => {
  const geo = getStoredGeolocation();

  // Cuando el navegador niega el permiso, la pantalla de login guarda una
  // ubicacion por defecto (CDMX). Mandarla como si fuera la del usuario ensucia
  // el registro de accesos, asi que en ese caso no se manda ninguna.
  if (!geo || geo.source === "default") return { latitude: null, longitude: null };

  const latitude = Number(geo.lat);
  const longitude = Number(geo.lng);

  return {
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
  };
};

export const buildLoginContext = async () => ({
  dispositivo: getDeviceLabel(),
  ipAddress: await getLocalIpAddress(),
  ...coordenadasDeSesion(),
  userAgent: getUserAgent(),
});
